<script setup lang="ts">
import { computed, ref } from 'vue';

const props = withDefaults(
  defineProps<{
    headingHtml?: string;
    subheadingHtml?: string;
    mediaUrl?: string;
    mediaType?: string;
    mediaAlt?: string;
    autoplayVideo?: boolean;
    bgColor?: string;
  }>(),
  {
    headingHtml: '',
    subheadingHtml: '',
    mediaUrl: '',
    mediaType: 'image',
    mediaAlt: '',
    autoplayVideo: true,
    bgColor: '',
  },
);

const isVideo = computed(() => props.mediaType === 'video');
const hasMedia = computed(() => !!(props.mediaUrl ?? '').trim());
const hasQuote = computed(() => strip(props.headingHtml).length > 0);
const quoteBg = computed(() => props.bgColor || '#f1f0ee');
const showControls = ref(false);

const show = computed(() => {
  return hasQuote.value || strip(props.subheadingHtml).length > 0 || hasMedia.value;
});

function strip(html: string | undefined): string {
  return (html ?? '').replace(/<[^>]*>/g, '').trim();
}
</script>

<template>
  <section
    v-if="show"
    class="pricing-voice py-40 max-md:overflow-x-clip md:py-60 lg:pb-12 lg:pt-80"
  >
    <div class="container max-md:px-0">
      <div class="grid gap-y-0 md:gap-y-28 lg:grid-cols-2 lg:gap-48">
        <!-- Media column -->
        <div
          v-if="hasMedia"
          class="relative h-350 overflow-hidden max-md:rounded-none md:rounded-[28px] lg:h-[650px]"
          @mouseenter="showControls = true"
          @mouseleave="showControls = false"
        >
          <video
            v-if="isVideo"
            class="voice-video block size-full object-cover"
            :src="mediaUrl"
            :controls="showControls"
            playsinline
            autoplay
            muted
            loop
          />
          <img
            v-else
            :src="mediaUrl"
            :alt="mediaAlt || 'Voice'"
            class="block size-full object-cover"
            loading="lazy"
            decoding="async"
          >
        </div>

        <!-- Quote column: flush with image on mobile (no gap, no radius); md+ restores padding/radius -->
        <div
          class="flex h-350 flex-col items-center justify-center text-center font-inter text-off-black-500 max-md:rounded-none max-md:p-0 md:rounded-[28px] md:px-48 md:py-40 lg:h-[650px] lg:px-70 lg:py-56"
          :style="{ backgroundColor: quoteBg }"
        >
          <h2
            v-if="headingHtml"
            class="voice-quote max-w-[380px] !font-inter text-22 font-normal leading-[1.5] lg:text-28"
            v-html="headingHtml"
          />

          <p
            v-if="subheadingHtml"
            class="mt-20 !font-inter-tight text-18 font-semibold leading-snug text-off-black-500 md:mt-32 md:text-20"
            v-html="subheadingHtml"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
@media (max-width: 767px) {
  .pricing-voice .container {
    max-width: none;
    padding-left: 0;
    padding-right: 0;
  }
}

.voice-video::-webkit-media-controls {
  opacity: 0;
  transition: opacity 0.3s ease;
}

.voice-video:hover::-webkit-media-controls {
  opacity: 1;
}
</style>
